export type Contract = {
  contractId?: string;
  updateOrNewPrices?: ENewOrUpdatePrices;
  dateChange?: boolean;
  currentlyFutureDatedContract?: boolean;
  contractPriceStartDate?: string;
  contractPriceEndDate?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractReference?: string;
  businessAreaId?: string;
  contractType?: string;
  accountManager?: string;
  supplier?: string;
  energyManagementType?: string;
  letterOfAuthority?: 'Y' | 'N';
  evergreen?: 'Y' | 'N';
  assetOwnership?: string;
  upliftTypeId?: string;
  upliftDate?: string;
  billingType?: string;
  billingFrequency?: string;
  billPeriod?: string;
  billingInSystem?: 'Y' | 'N';
  billDay?: number;
  billAdjustmentMonth?: string;
  leasePurchaseTerm?: string;
  standardDaysId?: string;
  removeFutureDatedServicePrices?: boolean;
  futureDatedPremiumPrices?: boolean;
  oohPrices?: OohPrice[];
  futureDatedOohPrices?: OohPrice[];
  removeFutureDatedOohPrices?: boolean;
  serviceList?: Service[];
  purchaseOrders?: PurchaseOrder[];
  features?: Feature[];
  mpidList?: Mpid[];
  premiumPrices?: Premium[];
  invoices?: Invoice[];
  meterPurchasePrices?: MeterPurchasePrice[];
};

export type Group = {
  customerGroupId?: string;
  customerGroupName?: string;
  invoices?: Invoice[];
  customerGroupTypeId?: string;
  companyNumber?: string;
  relatedGroup?: RelatedGroup;
  sapCustomerNumber?: string;
  address?: GroupAddress;
  contact?: GroupContact;
};

export type RelatedGroup = {
  customerGroupRelId?: string;
  customerRelatedGroupId?: string;
  relationshipType?: string;
};

export type GroupContact = {
  groupContactId?: string;
  groupContactName?: string;
  emailAddress?: string;
  jobTitle?: string;
  groupContactNumber?: string;
  extensionNumber?: string;
  primaryContact?: 'Y' | 'N';
  markedForDeletion?: boolean;
};

export type GroupAddress = {
  addressId?: string;
  addressName?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  town?: string;
  county?: string;
  postcode?: string;
};

export type ThirdPartyContact = {
  tpContactId?: string;
  industryMpid: string;
  contactDescription?: string;
  roleCode?: string;
  emailAddress?: string;
  phoneNumber?: string;
  contactName?: string;
};

export type Mpid = {
  tpContactId?: string;
  contractMpidEfd?: string;
  contractMpidEtd?: string;
  contact?: ThirdPartyContact;
  markedForDeletion?: boolean;
};

export type Invoice = {
  invoiceGroupId?: string;
  contractId?: string;
  defaultInvoiceGroup?: 'Y' | 'N';
  invoiceGroupDescription?: string;
  customerInvoiceName?: string;
  invoiceAddresse?: string;
  sapCustomerNumber?: string;
  useGroupAddress?: boolean;
  usePrimaryContact?: boolean;
  contact?: GroupContact;
  address?: GroupAddress;
  markedForDeletion?: boolean;
};

export type Feature = {
  featureTypeId?: string;
  contractFeatureStartDate?: string;
  contractFeatureEndDate?: string;
  attributes?: FeatureAttribute[];
};

export type FeatureAttribute = {
  attributeType?: string;
  attributeValue?: string;
  attributeName?: string;
};

export type PurchaseOrder = {
  poNumber?: string;
  poValue?: string;
  fromDate?: number;
  toDate?: string;
  poType?: string;
};

export type Service = {
  serviceId?: string;
  srvCodeId?: string;
  srvStartDate?: string;
  srvEndDate?: string;
  siteDetails?: SiteDetails[];
  markedForDeletion?: boolean;
};

export type SiteDetails = {
  servicePriceList?: { [priceType in EPriceType]?: ServicePrice };
  futureDatedServicePriceList?: { [priceType in EPriceType]?: ServicePrice };
  siteTypeId?: string;
  markedForDeletion?: boolean;
  newSiteDetail?: boolean;
};

export type MeterPurchasePrice = {
  contractPurchasePriceId?: string;
  siteTypeId?: string;
  annualBasePrice?: string;
  conPurchasePriceEfd?: string;
  conPurchasePriceEtd?: string;
  markedForDeletion?: boolean;
};

export type ServicePrice = {
  inherited: {
    conSrvPriceId?: string;
    annualBasePrice?: string;
    chargingPrice?: string;
  };
  nonInherited: {
    conSrvPriceId?: string;
    annualBasePrice?: string;
    chargingPrice?: string;
  };
};

export type OohPrice = {
  contractOohId?: string;
  dayOfWeek?: number;
  fromTime?: string;
  toTime?: string;
  hourlyBasePrice?: number;
  hourlyChargingPrice?: string;
  conOohPriceEfd?: string;
  conOohPriceEtd?: string;
  markedForDeletion?: boolean;
};

export type Premium = {
  conPremiumPriceId?: string;
  siteTypeId?: string;
  premiumJobId?: string;
  annualBasePrice?: string;
  chargingPrice?: string;
  serviceCode?: string;
  conPremiumPriceEtd?: string;
  conPremiumPriceEfd?: string;
  markedForDeletion?: boolean;
};

export type StandingDataPremiumJob = {
  premiumJobId: string;
  premiumJobDescription: string;
  defaultPremium: 'Y' | 'N';
  srvCodeId: string;
  defaultPrice: string;
};

export type StandingDataFeatureAttribute = {
  featureTypeId: string;
  attributeTypeId: string;
  attributeName: string;
};

export type StandingDataAttributeValue = {
  featureTypeId: string;
  attributeName: string;
  attributeValueVchar: string;
};

export type StandingDataBillingSchedule = {
  billingScheduleId: string;
  billingFrequencyId: string;
  billPeriod: string;
};

export type StandingDataIndustryMpid = {
  industryMpid: string;
};

export type StandingDataItem = {
  title: string;
  value: string;
};

export type ContractStandingData = {
  standingDataPremiumJobList: StandingDataPremiumJob[];
  standingDataFeatures: StandingDataItem[];
  standingDataFeatureAttributes: StandingDataFeatureAttribute[];
  standingDataAttributeValues: StandingDataAttributeValue[];
  standingDataSiteTypeList: StandingDataItem[];
  standingDataServiceCodeList: StandingDataItem[];
  standingDataAssetOwnershipList: StandingDataItem[];
  standingDataContractTypeList: StandingDataItem[];
  standingDataBillingTypeList: StandingDataItem[];
  standingDataEnergyManagementTypeList: StandingDataItem[];
  standingDataIndustryMpidList: StandingDataIndustryMpid[];
  standingDataStandardDaysList: StandingDataItem[];
  standingDataBillingScheduleList: StandingDataBillingSchedule[];
  standingDataServiceSubTypeMap: Record<string, string[]>;
};

export type StandingDataCustomerGroupType = {
  customerGroupTypeId: string;
  customerGroupTypeDesc: string;
};

export type GroupStandingData = {
  standingDataGroupTypeList: StandingDataCustomerGroupType[];
};

export enum EPriceType {
  GPRS = 'GPRS',
  GSM = 'GSM',
  PSTN = 'PSTN',
  PAKNET = 'PAKNET',
  CUSTOMER_OWNED = 'CustomerOwned'
}

export enum ENewOrUpdatePrices {
  NEW = 'New',
  UPDATE = 'Update'
}

export enum EPageViewType {
  ADD = 'ADD',
  EDIT = 'EDIT',
  VIEW = 'VIEW',
  RENEW = 'RENEW'
}

export type CustomerSearch = {
  searchCustomerGroupId?: string;
  searchSapNumber?: string;
};
